export interface DeviceLocation {
  latitude: number;
  longitude: number;
  accuracy?: number; // GPS accuracy in meters (lower = more precise)
}

/**
 * Gets the current device location with maximum precision.
 * Uses GPS hardware when available and retries if accuracy is poor.
 * @param options - Optional geolocation options
 * @param maxRetries - Maximum number of retries if accuracy is poor (default: 2)
 * @param minAccuracy - Minimum acceptable accuracy in meters (default: 50m)
 * @returns Promise resolving to location with full precision coordinates
 */
export const getDeviceLocation = (
  options?: PositionOptions,
  maxRetries: number = 2,
  minAccuracy: number = 50
): Promise<DeviceLocation | null> => {
  if (!navigator.geolocation) {
    console.warn('[getDeviceLocation] Geolocation is not supported by this browser');
    return Promise.resolve(null);
  }

  return new Promise((resolve) => {
    const defaultOptions: PositionOptions = {
      timeout: 20000, // 20 seconds - maximum timeout for GPS satellite acquisition
      maximumAge: 0, // Always get fresh position - no cached values
      enableHighAccuracy: true, // Force GPS hardware usage (not network-based)
      ...options,
    };

    const handleSuccess = (position: GeolocationPosition, attempt: number): void => {
      const accuracy = position.coords.accuracy;
      const latitude = position.coords.latitude;
      const longitude = position.coords.longitude;

      // Log GPS quality for monitoring
      if (accuracy <= 10) {
        console.info('[getDeviceLocation] High precision GPS acquired:', {
          accuracy: `${accuracy.toFixed(1)}m`,
          latitude,
          longitude,
        });
      } else if (accuracy <= 50) {
        console.info('[getDeviceLocation] Good GPS accuracy:', {
          accuracy: `${accuracy.toFixed(1)}m`,
          latitude,
          longitude,
        });
      } else {
        console.warn('[getDeviceLocation] Lower accuracy GPS:', {
          accuracy: `${accuracy.toFixed(1)}m`,
          latitude,
          longitude,
        });
      }

      // Retry if accuracy is poor and we have retries left
      if (accuracy > minAccuracy && attempt < maxRetries) {
        console.info(
          `[getDeviceLocation] Accuracy ${accuracy.toFixed(1)}m exceeds threshold ${minAccuracy}m, retrying... (attempt ${attempt + 1}/${maxRetries})`
        );
        setTimeout(() => attemptGetLocation(attempt + 1), 2000);
        return;
      }

      // Store coordinates with full precision (6-7 decimal places = ~10cm accuracy)
      resolve({
        latitude,
        longitude,
        accuracy,
      });
    };

    const handleError = (error: GeolocationPositionError, attempt: number): void => {
      // Retry on timeout/error if we have retries left
      if (attempt < maxRetries) {
        console.warn(
          `[getDeviceLocation] Error getting location (attempt ${attempt + 1}/${maxRetries}):`,
          error.message
        );
        setTimeout(() => attemptGetLocation(attempt + 1), 2000);
        return;
      }

      console.warn('[getDeviceLocation] Failed to get location after retries:', error.message);
      resolve(null);
    };

    const attemptGetLocation = (attempt: number): void => {
      navigator.geolocation.getCurrentPosition(
        (position) => handleSuccess(position, attempt),
        (error) => handleError(error, attempt),
        defaultOptions
      );
    };

    attemptGetLocation(0);
  });
};
